import { AreaIcon } from '@protector/icons';
import { Button, Form, Input } from 'antd';
import { COLOR_OPTIONS } from 'config/style/color-options';
import { getSelectedCompanyId } from 'core/core.selectors';
import type { TrapGroup } from '@features/regional-overview/digital-traps/domain';
import { useManageAssetGroupMutation } from 'querys/regional-overview/regional-overview.query';
import type { AssetGroup } from 'querys/regional-overview/regional-overview.query.models';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import styles from '../network-management.module.css';
import { mapTrapGroupToAssetGroup, mergeAssets } from '../network-management.utils';
import { useNetworkManagementStore } from '../network-management.zustand';
import { AssetDrawer, AssetFormItem, DrawerContent, Footer, SelectedTrapsContainer, SelectedTrapsText } from './manage-asset-group.styles';
import NetworkSelect from './network-select';

export const ManageAssetGroup = () => {
  const [t] = useTranslation();

  const selectedCompanyId = useSelector(getSelectedCompanyId);
  const { editingGroup, setEditingGroup, setSelectedTraps } = useNetworkManagementStore();
  const { mutate, isLoading } = useManageAssetGroupMutation();

  const [value, setValue] = useState(editingGroup?.name);
  const [assignedGroup, setAssignedGroup] = useState<AssetGroup | undefined>();

  const isEditingName = editingGroup?.management_type === 'NAME_EDIT';

  if (!editingGroup) {
    return null;
  }

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleSave = () => {
    if (!selectedCompanyId) {
      return;
    }

    const baseGroup = assignedGroup ?? { ...editingGroup, name: value ?? editingGroup.name };
    const managementType = editingGroup.management_type;

    const groupToMutate: AssetGroup = {
      ...baseGroup,
      organization_id: selectedCompanyId
    };

    mutate(
      { group: groupToMutate, managementType },
      {
        onSuccess: () => {
          setEditingGroup(undefined);
          setSelectedTraps([]);
        }
      }
    );
  };

  const handleCancel = () => {
    setEditingGroup(undefined);
  };

  const handleSelectChange = (selected: TrapGroup) => {
    const selectedAssetGroup = mapTrapGroupToAssetGroup(selected, 'ASSIGN_TRAPS');
    setAssignedGroup(mergeAssets(selectedAssetGroup, editingGroup.assets));
  };

  const isEditInvalid = isEditingName && !value;
  const isAssignInvalid = !isEditingName && !assignedGroup;

  const footer = (
    <Footer>
      <Button type='default' size='large' onClick={handleCancel}>
        {t('pages.regional_overview.digital_traps.asset_group_management.cancel')}
      </Button>
      <Button type='primary' size='large' onClick={handleSave} disabled={isLoading || isEditInvalid || isAssignInvalid} loading={isLoading}>
        {t('pages.regional_overview.digital_traps.asset_group_management.save')}
      </Button>
    </Footer>
  );

  return (
    <AssetDrawer
      title={t(
        isEditingName
          ? 'pages.regional_overview.digital_traps.asset_group_management.edit_network_title'
          : 'pages.regional_overview.digital_traps.asset_group_management.assign_traps_title'
      )}
      placement='right'
      onClose={handleCancel}
      footer={footer}
      size={400}
      classNames={{ wrapper: styles.fullscreenDrawerWrapper }}
      open={true}>
      {!isEditingName && (
        <SelectedTrapsContainer>
          <AreaIcon fill={COLOR_OPTIONS.NEUTRAL_60} />
          <SelectedTrapsText>
            {t('pages.regional_overview.digital_traps.network_management.selected_traps', {
              count: editingGroup.assets.length ?? 0
            })}
          </SelectedTrapsText>
        </SelectedTrapsContainer>
      )}
      <DrawerContent>
        <Form layout='vertical' initialValues={{ name: value }}>
          <AssetFormItem
            name='name'
            label={t('pages.regional_overview.digital_traps.asset_group_management.edit_network_name')}
            labelAlign='left'
            rules={[
              { required: true, message: t('pages.regional_overview.digital_traps.asset_group_management.edit_network_name_required') }
            ]}>
            {isEditingName && (
              <Input
                size='large'
                onChange={handleChangeName}
                placeholder={t('pages.regional_overview.digital_traps.asset_group_management.edit_network_name_placeholder')}
              />
            )}
            {!isEditingName && <NetworkSelect onChange={handleSelectChange} />}
          </AssetFormItem>
        </Form>
      </DrawerContent>
    </AssetDrawer>
  );
};
